import debugx from "debugx";
import { Position, Coordinate, Route, Waypoint, ReplayState } from "../types";
import { settingsService } from "./settingsService";
import { routeFetchService } from "./routeFetchService";

const debug = debugx("bikedash:simulation");
const debugPos = debugx("bikedash:sim-position");
const debugUpdate = debugx("bikedash:sim-update");
const debugHeading = debugx("bikedash:sim-heading");

// Convert legacy coordinates array to Route format
function createRouteFromCoordinates(
  id: string,
  name: string,
  description: string,
  coordinates: Coordinate[],
  estimatedDuration: number,
  averageSpeed: number,
): Route {
  // Only create waypoints for start and end points
  // Everything else is just track points for smooth following
  const waypoints: Waypoint[] = [];

  // Add start waypoint
  if (coordinates.length > 0) {
    waypoints.push({
      position: {
        lat: coordinates[0][1],
        lng: coordinates[0][0],
        timestamp: Date.now(),
      },
      name: "Start",
    });
  }

  // Add end waypoint if different from start
  const lastIdx = coordinates.length - 1;
  if (lastIdx > 0) {
    waypoints.push({
      position: {
        lat: coordinates[lastIdx][1],
        lng: coordinates[lastIdx][0],
        timestamp: Date.now(),
      },
      name: "End",
    });
  }

  return {
    id,
    name,
    description,
    waypoints,
    trackPoints: coordinates.map((coord) => ({
      lat: coord[1],
      lng: coord[0],
      timestamp: Date.now(),
    })),
    estimatedTime: estimatedDuration,
    totalDistance: averageSpeed * estimatedDuration,
    createdAt: Date.now(),
  };
}

export interface SimulationOptions {
  addNoise: boolean;
  varySpeed: boolean;
  fakeCompass: boolean;
  speedVariation: number; // 0-1, amount of speed variation
  noiseLevel: number; // meters, GPS accuracy noise
  updateRate: number; // Hz
  speedMultiplier: number; // Speed multiplier (0.5x to 10x)
  showRouteOnMap: boolean; // Show the route line on map
}

export const DEFAULT_SIMULATION_OPTIONS: SimulationOptions = {
  addNoise: true,
  varySpeed: true,
  fakeCompass: true,
  speedVariation: 0.3,
  noiseLevel: 5,
  updateRate: 1,
  speedMultiplier: 1.0, // Normal speed
  showRouteOnMap: true, // Show route by default
};

// Initialize with basic fallback routes - these will be replaced with real data when available
const fallbackRoutes = routeFetchService.getFallbackRoutes();

export const SIMULATION_ROUTES: Route[] = [
  createRouteFromCoordinates(
    "veloway",
    "Austin Veloway",
    "3.1 mile paved loop in South Austin for bikes only",
    fallbackRoutes["veloway"] || [
      [-97.8614, 30.1822],
      [-97.855, 30.1822],
      [-97.855, 30.175],
      [-97.8614, 30.175],
      [-97.8614, 30.1822],
    ],
    720,
    7.2,
  ),
  createRouteFromCoordinates(
    "lady-bird-lake",
    "Lady Bird Lake Trail",
    "10 mile loop around Lady Bird Lake through downtown Austin",
    fallbackRoutes["lady-bird-lake"] || [
      [-97.7505, 30.2624],
      [-97.7431, 30.258],
      [-97.728, 30.256],
      [-97.726, 30.263],
      [-97.743, 30.271],
      [-97.755, 30.266],
      [-97.7505, 30.2624],
    ],
    2400,
    5.6,
  ),
  createRouteFromCoordinates(
    "downtown-austin",
    "Downtown Austin Loop",
    "Urban ride through downtown Austin streets and the Capitol",
    fallbackRoutes["downtown-austin"] || [
      [-97.7431, 30.2672],
      [-97.74, 30.275],
      [-97.747, 30.275],
      [-97.747, 30.267],
      [-97.7431, 30.2672],
    ],
    1500,
    4.5,
  ),
];

export class SimulationService {
  private static instance: SimulationService;
  private isActive = false;
  private isPaused = false;
  private currentRoute: Route | null = null;

  static getInstance(): SimulationService {
    if (!SimulationService.instance) {
      SimulationService.instance = new SimulationService();
    }
    return SimulationService.instance;
  }
  private currentIndex = 0;
  private progress = 0; // 0-1 between current and next waypoint
  private timer: number | null = null;
  private headingTimer: number | null = null;
  private startTime = 0;
  private pausedTime = 0;
  private options: SimulationOptions = { ...DEFAULT_SIMULATION_OPTIONS };
  private onPositionUpdate: ((position: Position) => void) | null = null;
  private onTrackingModeChange:
    | ((mode: "simulation" | "auto") => Promise<void>)
    | null = null;

  // Heading smoothing
  private targetHeading: number = 0;
  private smoothedHeading: number = 0;
  private lastHeadingUpdate: number = 0;

  private getHeadingUpdateInterval(): number {
    const maxUpdateRate =
      settingsService.getSettings().user.maxUpdateRate || 30;
    return 1000 / maxUpdateRate; // Convert FPS to milliseconds
  }

  // Compass drift simulation
  private compassDriftTarget: number = 0;
  private compassDriftCurrent: number = 0;
  private lastCompassDriftUpdate: number = 0;

  // Lazy loading flag for routes
  private routesLoaded: boolean = false;

  private static readonly STORAGE_KEY = "bikedash_simulation_state";
  private static readonly OPTIONS_STORAGE_KEY = "bikedash_simulation_options";
  private static readonly STORAGE_VERSION = 1;

  constructor() {
    debug("SimulationService initialized");

    // Load saved options from localStorage
    this.loadSavedOptions();

    // Set up beforeunload handler to save state
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    // Always try to restore simulation state on load
    debug("Attempting to restore simulation state on load");
    // Load routes before restoring state (needed for simulation resume)
    this.routesLoaded = true;
    this.updateRoutesWithRealData();
    this.restoreState();

    // Note: Routes are loaded lazily when first accessed via getRoutes()
    // or immediately if resuming a simulation on load
  }

  /**
   * Attempt to fetch real route data from OSM APIs
   */
  private async updateRoutesWithRealData(): Promise<void> {
    debug("Attempting to fetch real route data");

    for (const route of SIMULATION_ROUTES) {
      try {
        // Try to fetch the full route first
        let realCoordinates = await routeFetchService.fetchRoute(
          route.name,
          {
            north: 30.35,
            south: 30.15,
            east: -97.65,
            west: -97.9,
          }, // Austin area bounding box
          true, // Force path-finding for debugging
        );

        // Fall back to waypoints if route fetch fails
        if (!realCoordinates || realCoordinates.length < 10) {
          debug(
            "Route fetch returned limited data, trying waypoints for:",
            route.name,
          );
          realCoordinates = await routeFetchService.fetchWaypoints(
            route.name,
            50, // Get more waypoints for better detail
          );
        }

        if (realCoordinates && realCoordinates.length > 3) {
          // Update trackPoints with real data
          route.trackPoints = realCoordinates.map((coord) => ({
            lat: coord[1],
            lng: coord[0],
            timestamp: Date.now(),
          }));
          debug(
            `Updated route with ${realCoordinates.length} points:`,
            route.name,
          );
        } else {
          debug(
            "Could not fetch real data for route, using fallback:",
            route.name,
          );
        }
      } catch (error) {
        debug.error("Error fetching route data:", error);
      }
    }
  }

  setRoute(routeId: string): boolean {
    const route = SIMULATION_ROUTES.find((r) => r.id === routeId);
    if (!route) {
      debug.error("Route not found:", routeId);
      return false;
    }

    this.currentRoute = route;
    this.currentIndex = 0;
    this.progress = 0;
    debug("Route set:", route.name);
    return true;
  }

  setOptions(options: Partial<SimulationOptions>): void {
    const oldUpdateRate = this.options.updateRate;
    const oldFakeCompass = this.options.fakeCompass;
    this.options = { ...this.options, ...options };
    debug("Simulation options updated:", this.options);

    // Save options to localStorage whenever they change
    this.saveOptions();

    // If update rate changed and simulation is running, restart the timer
    if (
      options.updateRate !== undefined &&
      options.updateRate !== oldUpdateRate &&
      this.timer
    ) {
      debug("Restarting timer with new update rate:", options.updateRate);
      clearInterval(this.timer);
      this.timer = window.setInterval(() => {
        if (!this.isPaused) {
          this.updatePosition();
        }
      }, 1000 / this.options.updateRate);
    }

    // If fakeCompass changed and heading timer is active, restart it
    if (
      options.fakeCompass !== undefined &&
      options.fakeCompass !== oldFakeCompass &&
      this.headingTimer
    ) {
      this.stopHeadingSmoothing();
      if (options.fakeCompass) {
        this.startHeadingSmoothing();
      }
    }
  }

  setPositionCallback(callback: (position: Position) => void): void {
    this.onPositionUpdate = callback;
  }

  setTrackingModeCallback(
    callback: (mode: "simulation" | "auto") => Promise<void>,
  ): void {
    this.onTrackingModeChange = callback;
  }

  async start(): Promise<boolean> {
    if (!this.currentRoute) {
      debug.error("No route selected for simulation");
      return false;
    }

    if (this.isActive) {
      debug.warn("Simulation already active");
      return false;
    }

    // Mark as active BEFORE switching providers to prevent race conditions
    this.isActive = true;
    this.isPaused = false;
    this.startTime = Date.now();
    this.pausedTime = 0;

    debug("Starting simulation:", {
      route: this.currentRoute.name,
      updateRate: this.options.updateRate,
    });

    // Set up the simulation timer and heading smoothing
    this.timer = window.setInterval(() => {
      if (!this.isPaused) {
        this.updatePosition();
      }
    }, 1000 / this.options.updateRate);

    // Start heading smoothing timer first
    this.startHeadingSmoothing();

    // Switch tracking mode to simulation AFTER setting up the simulation
    // This ensures positions are ready to flow when the provider switches
    if (this.onTrackingModeChange) {
      try {
        await this.onTrackingModeChange("simulation");
      } catch (error) {
        debug.error("Failed to switch tracking mode to simulation:", error);
        // Clean up on failure
        this.stop();
        return false;
      }
    }

    // Send initial position after provider is ready
    // Add a longer delay to ensure interpolation systems are initialized
    setTimeout(() => {
      this.updatePosition();
    }, 200);

    // Always save state when simulation starts
    this.saveState();

    return true;
  }

  pause(): void {
    if (!this.isActive) return;

    this.isPaused = !this.isPaused;
    debug(this.isPaused ? "Simulation paused" : "Simulation resumed");

    // Always save state when pausing/resuming
    this.saveState();
  }

  async stop(): Promise<void> {
    if (!this.isActive) return;

    this.isActive = false;
    this.isPaused = false;

    // Clear saved state when stopping
    this.clearSavedState();

    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.stopHeadingSmoothing();

    // Clear saved state when stopping
    this.clearSavedState();

    // Switch tracking mode back to auto
    if (this.onTrackingModeChange) {
      try {
        await this.onTrackingModeChange("auto");
      } catch (error) {
        debug.error("Failed to switch tracking mode to auto:", error);
      }
    }

    debug("Simulation stopped");
  }

  async reset(): Promise<void> {
    await this.stop();
    this.currentIndex = 0;
    this.progress = 0;
    debug("Simulation reset");
  }

  private updatePosition(): void {
    if (!this.currentRoute || !this.onPositionUpdate) return;

    const route = this.currentRoute;

    // Use trackPoints for smooth simulation
    const positions = route.trackPoints || [];
    if (positions.length === 0) {
      debug("No track points in route");
      return;
    }

    // Convert positions to coordinates for compatibility
    const coords: Coordinate[] = positions.map((p) => [p.lng!, p.lat!]);

    if (this.currentIndex >= coords.length - 1) {
      // Reached end, loop back or stop
      this.currentIndex = 0;
      this.progress = 0;
      debug("Route completed, looping back to start");
    }

    // Get current and next waypoints
    const current = coords[this.currentIndex];
    const next = coords[this.currentIndex + 1] || coords[0];

    // Interpolate position between waypoints
    const lat = this.lerp(current[1], next[1], this.progress);
    const lng = this.lerp(current[0], next[0], this.progress);

    // Calculate speed with variation and multiplier
    // Use estimated speed from total distance and time
    const averageSpeed =
      (route.totalDistance || 5000) / (route.estimatedTime || 600); // m/s
    let speed = averageSpeed * this.options.speedMultiplier;
    if (this.options.varySpeed) {
      const variation = this.options.speedVariation;
      const randomFactor = 1 + (Math.random() - 0.5) * variation * 2;
      speed *= randomFactor;

      // Add some hills (slow uphill, fast downhill)
      const hillFactor = Math.sin(this.progress * Math.PI * 4) * 0.2 + 1;
      speed *= hillFactor;
    }

    // Add GPS noise if enabled
    let noiseLat = lat;
    let noiseLng = lng;
    if (this.options.addNoise) {
      const noiseMeters = this.options.noiseLevel;
      const latNoiseMeters = (Math.random() - 0.5) * noiseMeters;
      const lngNoiseMeters = (Math.random() - 0.5) * noiseMeters;

      // Convert meters to degrees (rough approximation)
      noiseLat += latNoiseMeters / 111000;
      noiseLng += lngNoiseMeters / (111000 * Math.cos((lat * Math.PI) / 180));
    }

    // Calculate heading from current interpolated position to the next waypoint
    // This ensures the bike always points toward where it's going
    let targetHeading: number;

    if (this.progress < 0.8) {
      // While traveling between waypoints, point toward the next waypoint
      targetHeading = this.calculateHeading([lng, lat], next);
    } else {
      // When approaching the waypoint (last 20%), blend between current and next heading
      // for smoother transitions around corners
      const currentHeading = this.calculateHeading([lng, lat], next);
      const nextNext = coords[this.currentIndex + 2] || coords[0]; // Loop to start
      const nextHeading = this.calculateHeading(next, nextNext);

      // Blend factor: 0 at 80% progress, 1 at 100% progress
      const blendFactor = (this.progress - 0.8) / 0.2;

      // Interpolate between the two headings
      let angleDiff = nextHeading - currentHeading;
      if (angleDiff > 180) angleDiff -= 360;
      if (angleDiff < -180) angleDiff += 360;

      targetHeading = currentHeading + angleDiff * blendFactor;
      targetHeading = (targetHeading + 360) % 360;
    }

    // Apply fake compass drift to the TARGET before smoothing
    if (this.options.fakeCompass) {
      // Update compass drift slowly over time
      this.updateCompassDrift();
      targetHeading += this.compassDriftCurrent;
    }

    this.targetHeading = (targetHeading + 360) % 360;

    // ALWAYS use smoothed heading for consistency between 1fps and 10fps updates
    const smoothedHeading = this.getSmoothedHeading();
    const heading = smoothedHeading; // GPS heading uses smoothed value
    const compassHeading = smoothedHeading; // Compass uses same smoothed value

    // Create position object
    const position: Position = {
      lat: noiseLat,
      lng: noiseLng,
      accuracy: this.options.addNoise ? 5 + Math.random() * 10 : 3,
      speed,
      heading,
      compassHeading,
      timestamp: Date.now(),
      isComplete: true,
      isInterpolated: false, // This is a real simulated GPS position, not interpolated
    };

    debug("Generated simulated position");
    debugPos("Position data:", {
      lat: noiseLat.toFixed(6),
      lng: noiseLng.toFixed(6),
      speed: speed.toFixed(1),
      heading: heading.toFixed(1),
      compassHeading: compassHeading.toFixed(1),
    });

    this.onPositionUpdate(position);

    // Update progress with speed multiplier
    const segmentDistance = this.calculateDistance(current, next);
    // Note: speed already includes speedMultiplier from above
    const distancePerUpdate = speed / this.options.updateRate; // meters per update
    const progressPerUpdate = distancePerUpdate / segmentDistance; // fraction of segment

    this.progress += progressPerUpdate;

    if (this.progress >= 1) {
      this.progress = 0;
      this.currentIndex++;

      if (this.currentIndex >= coords.length - 1) {
        this.currentIndex = 0; // Loop back
      }

      // Always save state on waypoint change for resume
      this.saveState();
    }

    debug("Simulation position update");
    debugUpdate("Update data:", {
      waypoint: this.currentIndex,
      progress: this.progress.toFixed(2),
      speed: speed.toFixed(1),
      heading: heading.toFixed(0),
    });
  }

  private lerp(start: number, end: number, progress: number): number {
    return start + (end - start) * progress;
  }

  private calculateDistance(coord1: Coordinate, coord2: Coordinate): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (coord1[1] * Math.PI) / 180;
    const φ2 = (coord2[1] * Math.PI) / 180;
    const Δφ = ((coord2[1] - coord1[1]) * Math.PI) / 180;
    const Δλ = ((coord2[0] - coord1[0]) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  private calculateHeading(from: Coordinate, to: Coordinate): number {
    const lat1 = (from[1] * Math.PI) / 180;
    const lat2 = (to[1] * Math.PI) / 180;
    const deltaLng = ((to[0] - from[0]) * Math.PI) / 180;

    const y = Math.sin(deltaLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

    const heading = (Math.atan2(y, x) * 180) / Math.PI;
    return (heading + 360) % 360; // Normalize to 0-360
  }

  private startHeadingSmoothing(): void {
    if (this.headingTimer) return;

    this.smoothedHeading = this.targetHeading; // Initialize with current target
    this.lastHeadingUpdate = Date.now();

    this.headingTimer = window.setInterval(() => {
      if (!this.isPaused) {
        debugHeading("Timer tick");
        this.updateSmoothedHeading();
      }
    }, this.getHeadingUpdateInterval());

    debugHeading("Started heading smoothing at 30fps");
  }

  private stopHeadingSmoothing(): void {
    if (this.headingTimer) {
      clearInterval(this.headingTimer);
      this.headingTimer = null;
      debugHeading("Stopped heading smoothing");
    }
  }

  private updateSmoothedHeading(): void {
    debugHeading("updateSmoothedHeading called");

    const now = Date.now();
    const deltaTime = (now - this.lastHeadingUpdate) / 1000; // Convert to seconds
    this.lastHeadingUpdate = now;

    // Calculate the shortest angular distance between current and target heading
    const angleDiff = this.normalizeAngleDifference(
      this.targetHeading - this.smoothedHeading,
    );

    // Much slower interpolation for realistic heading changes
    // 0.5 means it takes ~2 seconds to complete a 90° turn
    // This matches realistic bike turning speeds
    const smoothingFactor = Math.min(deltaTime * 0.5, 1.0); // Much slower responsiveness

    const oldSmoothedHeading = this.smoothedHeading;

    // Update smoothed heading
    this.smoothedHeading += angleDiff * smoothingFactor;
    this.smoothedHeading = (this.smoothedHeading + 360) % 360; // Normalize to 0-360

    debugHeading(
      "Smoothing - target:",
      this.targetHeading.toFixed(1),
      "old:",
      oldSmoothedHeading.toFixed(1),
      "new:",
      this.smoothedHeading.toFixed(1),
    );

    // Emit a position update with the new compass heading (but keep same GPS position)
    this.emitCompassUpdate();
  }

  private normalizeAngleDifference(angleDiff: number): number {
    // Normalize angle difference to [-180, 180] to find shortest path
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;
    return angleDiff;
  }

  private getSmoothedHeading(): number {
    return this.smoothedHeading;
  }

  private updateCompassDrift(): void {
    const now = Date.now();

    // Update drift target every 5-10 seconds
    if (now - this.lastCompassDriftUpdate > 5000 + Math.random() * 5000) {
      // Set new drift target within ±5 degrees (realistic compass error)
      const oldTarget = this.compassDriftTarget;
      this.compassDriftTarget = (Math.random() - 0.5) * 10; // ±5 degrees
      this.lastCompassDriftUpdate = now;
      debugHeading(
        "New drift target:",
        this.compassDriftTarget.toFixed(1),
        "was:",
        oldTarget.toFixed(1),
      );
    }

    // Slowly drift towards target (takes about 30-60 seconds to reach target)
    const driftSpeed = 0.02; // How fast we drift towards target
    const driftDiff = this.compassDriftTarget - this.compassDriftCurrent;
    const oldDrift = this.compassDriftCurrent;
    this.compassDriftCurrent += driftDiff * driftSpeed;

    // Clamp drift to reasonable bounds (max ±8 degrees total error)
    this.compassDriftCurrent = Math.max(
      -8,
      Math.min(8, this.compassDriftCurrent),
    );

    // Debug drift changes
    if (Math.abs(this.compassDriftCurrent - oldDrift) > 0.01) {
      debugHeading(
        "Drift update:",
        "current:",
        this.compassDriftCurrent.toFixed(2),
        "target:",
        this.compassDriftTarget.toFixed(1),
        "diff:",
        driftDiff.toFixed(2),
      );
    }
  }

  private emitCompassUpdate(): void {
    if (!this.onPositionUpdate) return;

    const smoothedHeading = this.getSmoothedHeading();

    // Create a partial position update with just compass heading
    const compassUpdate: Position = {
      lat: undefined,
      lng: undefined,
      compassHeading: smoothedHeading,
      timestamp: Date.now(),
      isComplete: false, // Mark as partial update
    };

    debugHeading("Emitting compass update:", smoothedHeading.toFixed(1));
    this.onPositionUpdate(compassUpdate);
  }

  getState(): ReplayState {
    return {
      isActive: this.isActive,
      isPaused: this.isPaused,
      mode: "simulate",
      source: "custom",
      customTrack:
        this.currentRoute?.trackPoints?.map(
          (p) => [p.lng!, p.lat!] as Coordinate,
        ) || [],
      currentIndex: this.currentIndex,
      playbackSpeed: 1,
      loop: true,
      showGhost: false,
      showComparison: false,
      simulationOptions: this.options,
    };
  }

  getAvailableRoutes(): Route[] {
    // Lazy load routes on first access
    if (!this.routesLoaded) {
      this.routesLoaded = true;
      this.updateRoutesWithRealData();
    }
    return SIMULATION_ROUTES;
  }

  getCurrentRoute(): Route | null {
    return this.currentRoute;
  }

  getOptions(): SimulationOptions {
    return { ...this.options };
  }

  getProgress(): {
    currentIndex: number;
    progress: number;
    totalPositions: number; // Track points for smooth following
    totalWaypoints: number; // Major waypoints (start, end, etc)
    overallProgress: number; // 0-1 progress through entire route
  } {
    const totalPositions = this.currentRoute?.trackPoints?.length || 0;
    const totalWaypoints = this.currentRoute?.waypoints?.length || 0;
    const totalSegments = Math.max(totalPositions - 1, 1);
    const overallProgress =
      totalSegments > 0
        ? (this.currentIndex + this.progress) / totalSegments
        : 0;

    return {
      currentIndex: this.currentIndex,
      progress: this.progress,
      totalPositions,
      totalWaypoints,
      overallProgress: Math.min(overallProgress, 1),
    };
  }

  destroy(): void {
    debug("Destroying SimulationService");

    // Remove beforeunload handler
    window.removeEventListener("beforeunload", this.handleBeforeUnload);

    this.stop();
    this.onPositionUpdate = null;
    this.currentRoute = null;
  }

  private handleBeforeUnload = (): void => {
    // Always save state if simulation is active
    if (this.isActive) {
      this.saveState();
    }
  };

  // Save simulation state to localStorage
  private saveState(): void {
    if (!this.currentRoute) return;

    const state = {
      version: SimulationService.STORAGE_VERSION,
      isActive: this.isActive,
      isPaused: this.isPaused,
      routeId: this.currentRoute.id,
      currentIndex: this.currentIndex,
      progress: this.progress,
      options: this.options,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(
        SimulationService.STORAGE_KEY,
        JSON.stringify(state),
      );
      debug("Saved simulation state");
    } catch (error) {
      debug.error("Failed to save simulation state:", error);
    }
  }

  // Restore simulation state from localStorage
  private async restoreState(): Promise<void> {
    try {
      const storedState = localStorage.getItem(SimulationService.STORAGE_KEY);
      if (!storedState) {
        debug("No stored simulation state found");
        return;
      }

      const state = JSON.parse(storedState);
      debug("Found stored simulation state:", state);

      // Check version compatibility
      if (state.version !== SimulationService.STORAGE_VERSION) {
        debug("Stored state version mismatch, ignoring");
        localStorage.removeItem(SimulationService.STORAGE_KEY);
        return;
      }

      // Check if state is too old (more than 5 minutes)
      // This prevents old simulations from randomly resuming
      const age = Date.now() - state.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      debug("Simulation state age: %d minutes", ageMinutes);

      if (age > 5 * 60 * 1000) {
        debug("Stored state is too old (%d minutes), ignoring", ageMinutes);
        localStorage.removeItem(SimulationService.STORAGE_KEY);
        return;
      }

      // Restore state
      if (state.routeId && this.setRoute(state.routeId)) {
        this.currentIndex = state.currentIndex || 0;
        this.progress = state.progress || 0;
        this.options = { ...DEFAULT_SIMULATION_OPTIONS, ...state.options };

        debug("Restored simulation state:", {
          route: state.routeId,
          index: this.currentIndex,
          progress: this.progress,
        });

        // Auto-start if it was active
        if (state.isActive) {
          await this.start();
          if (state.isPaused) {
            this.pause();
          }
        }

        // Don't clear the state - let it persist for future reloads
        // It will be cleared when simulation is explicitly stopped
        debug("Simulation state restored successfully");
      } else {
        debug("Failed to set route, clearing invalid state");
        localStorage.removeItem(SimulationService.STORAGE_KEY);
      }
    } catch (error) {
      debug.error("Failed to restore simulation state:", error);
      localStorage.removeItem(SimulationService.STORAGE_KEY);
    }
  }

  // Check if simulation should resume on load
  private shouldResumeOnLoad(): boolean {
    const settings = settingsService.getSettings();
    return settings.user.resumeSimulationOnReload || false;
  }

  // Clear saved state
  clearSavedState(): void {
    localStorage.removeItem(SimulationService.STORAGE_KEY);
    debug("Cleared saved simulation state");
  }

  private saveOptions(): void {
    try {
      localStorage.setItem(
        SimulationService.OPTIONS_STORAGE_KEY,
        JSON.stringify(this.options),
      );
      debug("Saved simulation options to localStorage");
    } catch (error) {
      debug.error("Failed to save options:", error);
    }
  }

  private loadSavedOptions(): void {
    try {
      const saved = localStorage.getItem(SimulationService.OPTIONS_STORAGE_KEY);
      if (saved) {
        const savedOptions = JSON.parse(saved);
        this.options = { ...DEFAULT_SIMULATION_OPTIONS, ...savedOptions };
        debug("Loaded saved simulation options:", this.options);
      }
    } catch (error) {
      debug.error("Failed to load saved options:", error);
      this.options = { ...DEFAULT_SIMULATION_OPTIONS };
    }
  }
}

export function getSimulationService(): SimulationService {
  return SimulationService.getInstance();
}
